import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { clsx } from "clsx";
import { getFirestore, collection, query, where, orderBy, limit, onSnapshot, doc, getDoc, updateDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function NotificationHistory() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTweet, setSelectedTweet] = useState(null);
  const [currentUserCareer, setCurrentUserCareer] = useState(null);

  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setNotifications([]);
        setLoading(false);
        setError("No hay usuario autenticado");
        setCurrentUserCareer(null);
        return;
      }

      // Obtener la carrera del usuario
      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setCurrentUserCareer(userDoc.data().carrera || null);
        } else {
          console.error("Documento de usuario no encontrado");
          setError("No se encontró la información del usuario");
        }
      } catch (error) {
        console.error("Error al obtener la carrera del usuario:", error);
        setError("Error al cargar la información del usuario");
      }

      const q = query(
        collection(db, "NotificationHistory"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc"),
        limit(20)
      );

      const unsubscribeSnapshot = onSnapshot(q, (querySnapshot) => {
        const notifs = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          notifs.push({
            id: doc.id,
            title: data.title || "Sin título",
            description: data.body || "Sin contenido",
            timestamp: data.timestamp ? data.timestamp.toDate() : null,
            tipo: data.type || "general",
            url: data.url || "",
            read: data.read || false,
            fromUserId: data.fromUserId || "",
            tweetId: data.tweetId || ""
          });
        });
        setNotifications(notifs);
        setLoading(false);
        setError(null);
      }, (error) => {
        console.error("Error obteniendo notificaciones:", error);
        let errorMessage = "Error al cargar notificaciones";
        if (error.code === "failed-precondition" && error.message.includes("index")) {
          errorMessage = "Error de configuración en la base de datos. Por favor, contacta al administrador.";
        } else {
          errorMessage = `Error al cargar notificaciones: ${error.message}`;
        }
        setError(errorMessage);
        setLoading(false);
      });

      return () => unsubscribeSnapshot();
    }, (authError) => {
      console.error("Error en autenticación:", authError);
      setError("Error en autenticación");
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, [auth, db]);

  // Función para marcar notificación como leída
  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "NotificationHistory", notificationId);
      await updateDoc(notificationRef, { read: true });
    } catch (error) {
      console.error("Error al marcar notificación como leída:", error);
    }
  };

  // Función para obtener el contenido del tweet
  const fetchTweetContent = async (tweetId, tipo) => {
    try {
      let tweetRef;
      if (tipo === "career" && currentUserCareer) {
        tweetRef = doc(db, `tweetsByCareer/${currentUserCareer}/tweets`, tweetId);
      } else {
        tweetRef = doc(db, "tweets", tweetId);
      }
      const tweetDoc = await getDoc(tweetRef);
      if (tweetDoc.exists()) {
        return tweetDoc.data();
      } else {
        console.error("Tweet no encontrado");
        return null;
      }
    } catch (error) {
      console.error("Error al obtener el tweet:", error);
      return null;
    }
  };

  // Manejar clic en notificación
  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id);

    if (notification.tipo === "message") {
      navigate(notification.url);
    } else if ((notification.tipo === "tweet" || notification.tipo === "career") && notification.tweetId) {
      const tweetData = await fetchTweetContent(notification.tweetId, notification.tipo);
      if (tweetData) {
        setSelectedTweet({
          ...notification,
          description: tweetData.content || "Contenido no disponible",
          userName: tweetData.userName || "Usuario desconocido",
          date: tweetData.date ? tweetData.date.toDate() : null,
          media: tweetData.media || []
        });
        setModalOpen(true);
      } else {
        setError("No se pudo cargar el contenido del tweet");
      }
    } else if (notification.url) {
      window.open(notification.url, "_blank");
    }

    setOpen(false);
  };

  function formatTime(timestamp) {
    if (!timestamp) return "Fecha desconocida";
    const now = new Date();
    const diff = (now - timestamp) / 1000;

    if (diff < 60) return "hace unos segundos";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} horas`;
    return timestamp.toLocaleDateString("es-DO", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Historial de notificaciones"
      >
        <Bell className="w-6 h-6" />
        {notifications.some(n => !n.read) && (
          <span className="absolute top-0 right-0 block w-3 h-3 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-800 animate-pulse" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Notificaciones</h3>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-500 hover:text-red-500 transition focus:outline-none"
              aria-label="Cerrar panel de notificaciones"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400 animate-pulse">
              Cargando notificaciones...
            </div>
          ) : error ? (
            <div className="p-4 text-center text-sm text-red-500 dark:text-red-400">{error}</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No hay notificaciones.
            </div>
          ) : (
            <ul className="max-h-60 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={clsx(
                    "px-4 py-3 transition cursor-pointer flex items-start space-x-3",
                    n.read
                      ? "bg-gray-50 dark:bg-gray-900 opacity-75"
                      : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  )}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="flex-shrink-0">
                    <div
                      className={clsx(
                        "w-2 h-2 rounded-full mt-1",
                        n.read ? "bg-gray-400" : "bg-blue-500 animate-pulse"
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{n.title}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{n.description}</p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-400 dark:text-gray-500">{formatTime(n.timestamp)}</span>
                      <span className="text-xs font-medium text-blue-500">{n.tipo}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {modalOpen && selectedTweet && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-xl md:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedTweet.userName}</h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-500 hover:text-red-500 transition focus:outline-none"
                  aria-label="Cerrar modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-base text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
                {selectedTweet.description}
              </p>
              {selectedTweet.media && selectedTweet.media.length > 0 && (
                <div className="mb-4">
                  {selectedTweet.media.map((mediaUrl, index) => (
                    <img
                      key={index}
                      src={mediaUrl}
                      alt={`Media ${index + 1}`}
                      className="w-full h-auto rounded-lg shadow-md max-h-96 object-contain"
                    />
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {formatTime(selectedTweet.date || selectedTweet.timestamp)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}